import { useState, useMemo } from 'react';

const FORE_RATE = 0.0155; // 1.55% interchange-plus target

function formatCurrency(value: number): string {
  return '$' + Math.round(value).toLocaleString('en-US');
}

export default function SavingsCalculator() {
  const [volume, setVolume] = useState(50000);
  const [currentRate, setCurrentRate] = useState(2.9);

  const annualSavings = useMemo(() => {
    const currentAnnualFees = volume * 12 * (currentRate / 100);
    const foreAnnualFees = volume * 12 * FORE_RATE;
    return Math.max(0, currentAnnualFees - foreAnnualFees);
  }, [volume, currentRate]);

  const volumeMin = 10000;
  const volumeMax = 500000;
  const rateMin = 1.5;
  const rateMax = 5.0;

  const volumePct = ((volume - volumeMin) / (volumeMax - volumeMin)) * 100;
  const ratePct = ((currentRate - rateMin) / (rateMax - rateMin)) * 100;

  return (
    <section className="py-28 px-5 md:px-[75px]" style={{ backgroundColor: '#f9f9f7' }}>
      <div className="max-w-[1400px] mx-auto">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* Left — copy + sliders */}
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.25em] font-medium mb-5"
              style={{ fontFamily: "'Google Sans Flex', sans-serif", color: '#a8894a' }}
            >
              Savings Estimator
            </p>
            <h2
              className="text-4xl md:text-5xl lg:text-[54px] font-light leading-[1.1] mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: '#061e45' }}
            >
              Calculate Your Hidden Savings.
            </h2>
            <p
              className="text-[15px] leading-relaxed mb-12"
              style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400, color: 'rgba(6,30,69,0.5)' }}
            >
              Most processors use tiered pricing or opaque surcharges. We use true interchange-plus pricing. See what a fair margin could save you annually.
            </p>

            {/* Slider card */}
            <div
              className="p-8 md:p-10"
              style={{ background: '#ffffff', border: '1px solid rgba(6,30,69,0.08)' }}
            >
              {/* Monthly Volume */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-5">
                  <span
                    className="text-[13px] font-semibold uppercase tracking-[0.08em]"
                    style={{ fontFamily: "'Google Sans Flex', sans-serif", color: '#061e45' }}
                  >
                    Monthly Processing Volume
                  </span>
                  <span
                    className="text-[15px] font-semibold"
                    style={{ fontFamily: "'Google Sans Flex', sans-serif", color: '#061e45' }}
                  >
                    {formatCurrency(volume)}
                  </span>
                </div>
                <div className="relative h-[3px] rounded-full" style={{ background: 'rgba(6,30,69,0.1)' }}>
                  <div
                    className="absolute left-0 top-0 h-full rounded-full"
                    style={{ width: `${volumePct}%`, background: '#061e45' }}
                  />
                  <input
                    type="range"
                    min={volumeMin}
                    max={volumeMax}
                    step={5000}
                    value={volume}
                    onChange={e => setVolume(Number(e.target.value))}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer h-full"
                    style={{ margin: 0 }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 pointer-events-none"
                    style={{
                      left: `calc(${volumePct}% - 8px)`,
                      background: '#ffffff',
                      borderColor: '#061e45',
                    }}
                  />
                </div>
              </div>

              {/* Effective Rate */}
              <div>
                <div className="flex items-center justify-between mb-5">
                  <span
                    className="text-[13px] font-semibold uppercase tracking-[0.08em]"
                    style={{ fontFamily: "'Google Sans Flex', sans-serif", color: '#061e45' }}
                  >
                    Current Effective Rate
                  </span>
                  <span
                    className="text-[15px] font-semibold"
                    style={{ fontFamily: "'Google Sans Flex', sans-serif", color: '#061e45' }}
                  >
                    {currentRate.toFixed(2)}%
                  </span>
                </div>
                <div className="relative h-[3px] rounded-full" style={{ background: 'rgba(6,30,69,0.1)' }}>
                  <div
                    className="absolute left-0 top-0 h-full rounded-full"
                    style={{ width: `${ratePct}%`, background: '#061e45' }}
                  />
                  <input
                    type="range"
                    min={rateMin}
                    max={rateMax}
                    step={0.05}
                    value={currentRate}
                    onChange={e => setCurrentRate(Number(e.target.value))}
                    className="absolute inset-0 w-full opacity-0 cursor-pointer h-full"
                    style={{ margin: 0 }}
                  />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 pointer-events-none"
                    style={{
                      left: `calc(${ratePct}% - 8px)`,
                      background: '#ffffff',
                      borderColor: '#061e45',
                    }}
                  />
                </div>
                <p
                  className="mt-4 text-[11px] uppercase tracking-[0.12em]"
                  style={{ fontFamily: "'Google Sans Flex', sans-serif", color: 'rgba(6,30,69,0.3)' }}
                >
                  Effective rate = Total Fees / Total Volume
                </p>
              </div>
            </div>
          </div>

          {/* Right — results card */}
          <div
            className="p-8 md:p-12 flex flex-col justify-between min-h-[420px]"
            style={{ background: 'linear-gradient(135deg, #1a3870 0%, #061e45 100%)' }}
          >
            <div>
              <p
                className="text-[11px] uppercase tracking-[0.22em] font-medium mb-6"
                style={{ fontFamily: "'Google Sans Flex', sans-serif", color: 'rgba(255,255,255,0.45)' }}
              >
                Estimated Annual Savings
              </p>
              <div
                className="text-5xl md:text-6xl lg:text-7xl font-light leading-none mb-10 transition-all duration-300 break-all"
                style={{ fontFamily: "'Playfair Display', serif", color: '#d3b366' }}
              >
                {formatCurrency(annualSavings)}
              </div>

              <ul className="flex flex-col gap-5 mb-12">
                {[
                  'Based on interchange-plus wholesale pricing',
                  'No hidden PCI non-compliance fees',
                  'Month-to-month agreement, no cancellation fees',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                      <i
                        className="ri-checkbox-circle-line text-lg"
                        style={{ color: '#d3b366' }}
                      />
                    </div>
                    <span
                      className="text-[14px] leading-snug"
                      style={{ fontFamily: "'Google Sans Flex', sans-serif", fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href="/contact"
              className="w-full inline-flex items-center justify-center gap-3 text-[12px] uppercase tracking-[0.18em] font-semibold px-8 py-5 transition-all duration-200 cursor-pointer text-center"
              style={{ background: '#d3b366', color: '#061e45' }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#c4a455';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLAnchorElement).style.background = '#d3b366';
              }}
            >
              Claim Your Savings Rate
              <i className="ri-arrow-right-line" />
            </a>
          </div>

        </div>
      </div>
    </section>
  );
}
